import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { mutateUserNotifications, loadUserNotificationPreferences } from './notificationService'
import { callable } from './callable'
import type { NotificationCategory, NotificationPreferenceMatrix, UserNotificationPreferences } from '../types/schema'

export interface FamilyInboxRow {
  rowId: string
  notificationId: string
  title: string
  body: string
  category: NotificationCategory | string
  deepLink: string | null
  childKey: string | null
  createdAtMs: number
  unread: boolean
}

const timestampMillis = (value: unknown): number => {
  if (typeof value === 'number') return value
  if (value && typeof value === 'object') {
    if ('toMillis' in value && typeof (value as { toMillis: unknown }).toMillis === 'function') {
      try {
        return (value as { toMillis: () => number }).toMillis()
      } catch {
        return 0
      }
    }
    if ('seconds' in value && typeof (value as { seconds: unknown }).seconds === 'number') {
      return (value as { seconds: number }).seconds * 1000
    }
    if ('toDate' in value && typeof (value as { toDate: unknown }).toDate === 'function') {
      try {
        return (value as { toDate: () => Date }).toDate().getTime()
      } catch {
        return 0
      }
    }
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return isNaN(parsed) ? 0 : parsed
  }
  return 0
}

export const parseChildKeyFromDeepLink = (deepLink: string | null | undefined): string | null => {
  if (!deepLink) return null
  const match = deepLink.match(/[?&]child=([^&#]+)/)
  if (match) {
    try {
      return decodeURIComponent(match[1])
    } catch {
      return match[1]
    }
  }
  return null
}

export const subscribeToFamilyInbox = (
  uid: string,
  onChange: (rows: FamilyInboxRow[]) => void,
): (() => void) => {
  if (!uid) {
    onChange([])
    return () => {}
  }

  const itemsRef = query(
    collection(firestoreDb, 'notifications', uid, 'items'),
    orderBy('createdAt', 'desc'),
    limit(100),
  )

  return onSnapshot(
    itemsRef,
    (snapshot) => {
      const rows: FamilyInboxRow[] = []
      for (const doc of snapshot.docs) {
        const data = doc.data() as Record<string, unknown>
        if (data.dismissedAt || data.clearedAt) continue

        const createdAtMs = timestampMillis(data.createdAt)
        const unread = !data.readAt
        const isDigest = data.isDigest === true || data.category === 'DIGEST' || Array.isArray(data.digestItems)

        if (isDigest && Array.isArray(data.digestItems)) {
          data.digestItems.forEach((item: Record<string, unknown>, index: number) => {
            const deepLink = typeof item.deepLink === 'string' ? item.deepLink : null
            rows.push({
              rowId: `${doc.id}_${index}`,
              notificationId: doc.id,
              title: typeof item.title === 'string' ? item.title : '',
              body: typeof item.body === 'string' ? item.body : '',
              category: typeof item.category === 'string' ? (item.category as NotificationCategory) : 'DIGEST',
              deepLink,
              childKey: parseChildKeyFromDeepLink(deepLink),
              createdAtMs,
              unread,
            })
          })
        } else {
          const deepLink =
            typeof data.deepLink === 'string'
              ? data.deepLink
              : typeof data.deeplink === 'string'
                ? data.deeplink
                : null
          rows.push({
            rowId: doc.id,
            notificationId: doc.id,
            title: typeof data.title === 'string' ? data.title : '',
            body: typeof data.body === 'string' ? data.body : '',
            category: typeof data.category === 'string' ? (data.category as NotificationCategory) : 'UPDATES',
            deepLink,
            childKey: parseChildKeyFromDeepLink(deepLink),
            createdAtMs,
            unread,
          })
        }
      }
      onChange(rows)
    },
    (err) => {
      console.warn('[FamilyNotificationService] subscription failed:', err)
    },
  )
}

export const markFamilyInboxRead = async (notificationIds: string[]) => {
  if (!notificationIds || notificationIds.length === 0) {
    return { updated: 0, deepLink: null }
  }
  return mutateUserNotifications('READ', notificationIds)
}

export type EduAlertCategory = 'EDU_ATTENDANCE' | 'EDU_LEAVE' | 'EDU_HOMEWORK' | 'EDU_CLASSES' | 'EDU_TODAY' | 'EDU_REPORTS' | 'EDU_FEES' | 'EDU_BOOKINGS' | 'EDU_PROGRESS' | 'EDU_ANNOUNCEMENTS' | 'EDU_MESSAGES' | 'EDU_EVENTS'

const defaultEduCategoryPreference = (category: EduAlertCategory): NotificationPreferenceMatrix => ({
  inApp: true,
  email: false,
  push: category !== 'EDU_HOMEWORK' && category !== 'EDU_TODAY' && category !== 'EDU_ANNOUNCEMENTS',
  digest: category === 'EDU_HOMEWORK' || category === 'EDU_TODAY' || category === 'EDU_ANNOUNCEMENTS',
})

let alertCategoryQueue: Promise<void> = Promise.resolve()

export const setFamilyAlertCategory = (
  uid: string,
  category: EduAlertCategory,
  enabled: boolean,
): Promise<void> => {
  const next = alertCategoryQueue
    .catch(() => {})
    .then(async () => {
      const prefs = await loadUserNotificationPreferences(uid)
      const existingCategory = prefs.categories[category] ?? defaultEduCategoryPreference(category)
      const updatedPrefs: UserNotificationPreferences = {
        ...prefs,
        categories: {
          ...prefs.categories,
          [category]: {
            ...existingCategory,
            inApp: enabled,
          },
        },
      }
      await callable('updateNotificationPreferences', updatedPrefs)
    })

  alertCategoryQueue = next
  return next
}

